import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  input,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as domtoimage from 'dom-to-image';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { NgxGaugeModule } from 'ngx-gauge';
import { SharedModule } from 'src/app/shared/shared.module';

import { GaugeValueKPI } from '../../types';
import { GaugeValueModalComponent } from './gauge-value-modal/gauge-value-modal.component';

@Component({
  selector: 'app-gauge-value',
  templateUrl: './gauge-value.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgxGaugeModule,
    SharedModule,
    NgClass,
  ],
})
export class GaugeValueComponent {
  readonly modalContent = viewChild<ElementRef>('modalContent');

  readonly data = input.required<GaugeValueKPI>();

  readonly loading = input(false);

  private readonly matDialog = inject(MatDialog);

  downloadImage(): void {
    const contentElement = this.modalContent()?.nativeElement as
      | HTMLElement
      | undefined;

    if (contentElement !== undefined && contentElement !== null) {
      const options = {
        width: contentElement.offsetWidth,
        height: contentElement.offsetHeight,
        scale: 1.5,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top center',
        },
      };

      domtoimage
        .toPng(contentElement, options)
        .then((dataUrl: string) => {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'component-image.png';
          link.click();
        })
        .catch((error) => {
          console.warn('Error', error);
        });
    }
  }

  openModal(): void {
    this.matDialog.open<GaugeValueModalComponent, GaugeValueKPI>(
      GaugeValueModalComponent,
      {
        width: 'calc(100vw - 1rem)',
        hasBackdrop: true,
        data: this.data(),
      },
    );
  }

  async downloadXlsx(): Promise<void> {
    const d = this.data();
    const thresholds = [0, 25, 50, 75, 100];

    const rows: Array<[string, number]> = [
      ['Current Value', d.value],
      ['Max Value', d.maxValue],
      ...thresholds.map((pct): [string, number] => [
        `Threshold ${pct}%`,
        d.maxValue * (pct / 100),
      ]),
    ];

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Gauge Data');
    worksheet.addRows([['Metric', 'Value'], ...rows]);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'gauge-data.xlsx',
    );
  }
}
